import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "A day is not 60*60*24 seconds long",
  "description": "",
  "published": "2015-11-18T00:00:00.000Z",
  "redirect_from": ["/blog/a-day-is-not-606024-seconds-long/swizec/6755"],
  "categories": "dst, Technical, timezones",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`I was fixing some code written by an intrepid junior the other day and came upon this beauty:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var day = 1000 * 60 * 60 * 24;
var week = day * 7;

var yesterday = new Date(today.getTime() - day);
var lastWeek = new Date(today.getTime() - week);
`}</code></pre>
    <p>{`Looks pretty good, right? It calculates the timestamp for yesterday and a timestamp for last week by deducting the amount of milliseconds in a day and a week from the current time. Clever.`}</p>
    <p>{`But wait … Does it smell bad to you? I'll give you a minute.`}</p>
    <p>{`...`}</p>
    <p>{`How about now? Are your nose hairs sizzling yet?`}</p>
    <p>{`Maybe not. I mean, we've all written code like this. Maybe not recently, but for sure when we were starting out. Even the internet didn't smell anything.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Pop quiz: Why is this code wrong?`}<br parentName="p"></br><br parentName="p"></br>{`var day = 1000*60*60*24`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/661675783199453184"
      }}>{`November 3, 2015`}</a></blockquote>
    <p>{`There’s less context, but the smelliest line of code is still there.`}</p>
    <p>{`Answers ranged from the slightly missing the point:`}</p>
    <p>{`<`}{`tweet gone missing>`}</p>
    <p>{`To the snarky:`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}><a parentName="p" {...{
          "href": "https://twitter.com/Swizec"
        }}>{`@swizec`}</a>{` You should use ES6's const instead of var...`}</p>{`— Anže Pečar (@Smotko) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Smotko/status/661676777534857217"
      }}>{`November 3, 2015`}</a></blockquote>
    <p>{`And even some that tried to do a linter's job.`}</p>
    <p>{`They all have `}<strong parentName="p">{`a`}</strong>{` point, but none of them got `}<strong parentName="p">{`the`}</strong>{` point.`}</p>
    <p>{`Days aren't `}<inlineCode parentName="p">{`60*60*24`}</inlineCode>{` seconds long. They are `}<inlineCode parentName="p">{`1 day`}</inlineCode>{` long, which on most days maps to `}<inlineCode parentName="p">{`60*60*24 = 86400`}</inlineCode>{` seconds. But not on all days.`}</p>
    <p>{`Thanks to the wonder that is daylight savings time (DST), we have a day that's only 23 hours long. We also have a day that's 25 hours long. Then, every few years, we have a day that's 86,401 seconds long. In theory, we could even have a day that's 86,399 seconds long. It’s unlikely, but it’s possible.`}</p>
    <p>{`But, I mean, who cares, right? It's just an hour here, an hour there, and maybe a second when no one’s paying attention. Nobody's going to notice. `}<em parentName="p">{`24 hours ago`}</em>{` should still mean `}<em parentName="p">{`yesterday`}</em>{`.`}</p>
    <p>{`Here's the thing, though. Just because it’s `}<em parentName="p">{`yesterday`}</em>{` doesn’t mean it’s also `}<em parentName="p">{`1 day ago`}</em>{`.`}</p>
    <p>{`Let’s say this code runs between 2 am and 3 am on the days when daylight savings time goes away. First of all, not all countries do the change; not even all US states do. Second of all, there are `}<strong parentName="p">{`two`}</strong>{` instances of `}<em parentName="p">{`the hour between 2 am and 3 am`}</em>{`.`}</p>
    <p>{`Let me show you some examples:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`new Date((new Date("<a href="tel:2015110102">2015-11-01 02</a>:59 pst").getTime()-1000*60*60*24))
// Sat Oct 31 2015 03:59:00 GMT-0700 (PDT)
new Date((new Date("<a href="tel:2015110102">2015-11-01 02</a>:59 pdt").getTime()-1000*60*60*24))
// Sat Oct 31 2015 02:59:00 GMT-0700 (PDT)
`}</code></pre>
    <p>{`Subtracting 24 hours from PST (Pacific Standard Time) gives you the day before, but an hour later. Subtracting 24 hours from PDT (Pacific Daylight Time) gives you the day before at the same time. This timezone name-change is how you know which side of DST you're on.`}</p>
    <p>{`The same example without specifying a timezone chooses your local timezone (which for me is PST):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`new Date(new Date("2015-11-01 02:59").getTime() - 1000 * 60 * 60 * 24);
// Sat Oct 31 2015 03:59:00 GMT-0700 (PDT)
`}</code></pre>
    <p>{`Now let's see what happens in March, when daylight savings time goes back into effect:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`new Date(new Date("2015-03-08 02:30 pdt").getTime() - 1000 * 60 * 60 * 24);
// Sat Mar 07 2015 01:30:00 GMT-0800 (PST)
new Date(new Date("2015-03-08 02:30 pst").getTime() - 1000 * 60 * 60 * 24);
// Sat Mar 07 2015 02:30:00 GMT-0800 (PST)
`}</code></pre>
    <p>{`We didn't get the wrong date because JavaScript is awesome and automatically covers up the mess with timezone changes. It knows that on these key days, PST becomes PDT and vice-versa.`}</p>
    <p>{`At least, this is what happened when I ran the code in San Francisco. When I tried in Denver, a few days later, it looked like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`new Date(new Date("2015-11-01 02:59 pst").getTime() - 1000 * 60 * 60 * 24);
// Sat Oct 31 2015 04:59:00 GMT-0600 (MDT)
new Date(new Date("2015-11-01 02:59 pdt").getTime() - 1000 * 60 * 60 * 24);
// Sat Oct 31 2015 03:59:00 GMT-0600 (MDT)
new Date(new Date("2015-11-01 01:59 pdt").getTime() - 1000 * 60 * 60 * 24);
// Sat Oct 31 2015 02:59:00 GMT-0600 (MDT)
new Date(new Date("2015-11-01 01:59 pst").getTime() - 1000 * 60 * 60 * 24);
// Sat Oct 31 2015 03:59:00 GMT-0600 (MDT)
`}</code></pre>
    <p>{`Notice the funny part? At 2:59, we’re hit by San Francisco’s DST change. At 1:59, we’re hit with Denver’s DST change. That’s because Denver is on Mountain Time, which is an hour ahead of Pacific Time. So Denver goes through the DST change an hour before San Francisco does. Mind = blown.`}</p>
    <p>{`Now, this wasn’t too bad because JavaScript is amazing and knows when to automatically switch timezones. But what if you’re in Arizona? That state’s on Mouintain Time, and `}<em parentName="p">{`most of it`}</em>{` doesn’t observe daylight savings. Now JavaScript needs to know a whole lot about your user. Let’s hope it does.`}</p>
    <p>{`You might not care about any of this, but if your job is to capture `}<em parentName="p">{`all of X from yesterday`}</em>{`, then you're in for a ride when your boss or client says, "Errrr, there seems to be a whole hour of events missing. That's 10,000 datapoints. Where did they go?"`}</p>
    <p>{`Then there are leap seconds.`}</p>
    <p>{`In a way, leap seconds are less of a problem because you can only get it wrong by a second; but, when you're calculating with Unix epoch times (which is what `}<inlineCode parentName="p">{`getTime()`}</inlineCode>{` does, by the way), you don't have the luxury of timezones. Not that timezones would help for leap seconds.`}</p>
    <p>{`You'd think a system that counts seconds since January 1st, 1970, wouldn't have this problem. Unfortunately, you’d be wrong.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Observe that when a positive leap second occurs (i.e., when a leap second is inserted), the Unix time numbers repeat themselves. The Unix time number 915148800.50 is ambiguous. It can refer either to the instant in the middle of the leap second, or to the instant one second later, half a second after midnight UTC.`}</p>
      <p parentName="blockquote">{`&embdash; Wikipedia (`}<a parentName="p" {...{
          "href": "https://en.wikipedia.org/wiki/Unix_time"
        }}>{`https://en.wikipedia.org/wiki/Unix_time`}</a>{`)`}</p>
    </blockquote>
    <p>{`Let's see what JavaScript will do in this situation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`new Date(new Date("1999-01-01T00:00:00.00").getTime() - 1000 * 60 * 60 * 24);
// Wed Dec 30 1998 16:00:00 GMT-0800 (PST)
`}</code></pre>
    <p>{`Ok. That’s bad. We've lost an entire day. I’m pretty sure that’s going to be a problem. If your boss or client was unhappy about the 10,000 datapoints you missed when you got it wrong by an hour, how will they feel about losing 240,000 datapoints?`}</p>
    <p>{`Oh, and by the way, stop using Unix timestamps as unique identifiers because they’re not unique.`}</p>
    <p>{`All that said, there were two or three people that put garlic around their necks, tossed salt over their shoulders, and ran as fast as their legs would carry them.`}</p>
    <p>{`<`}{`tweet gone missing>`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}><a parentName="p" {...{
          "href": "https://twitter.com/Swizec"
        }}>{`@Swizec`}</a>{` This is a minefield, especially for distributed systems. See falsehoods programmers believe about time: `}<a parentName="p" {...{
          "href": "https://t.co/C3CCMMhIib"
        }}>{`https://t.co/C3CCMMhIib`}</a></p>{`— galar (@galar) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/galar/status/661701633030647809"
      }}>{`November 4, 2015`}</a></blockquote>
    <p>{`You should read the falsehoods article. Dealing with time `}<em parentName="p">{`is`}</em>{` a minefield.`}</p>
    <p>{`Oh, one more thing. Relativistic effects come into play for rocket guidance systems, things running on space stations, and anything moving quickly. That's when a second is no longer a second.`}</p>
    <p>{`Let’s hope neither the military nor any astronauts ever use your real-time smartphone app or website.`}</p>
    <p>{`P.S.: There's internet on the International Space Station. ;)`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      